import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { customStyles } from "../Utility/Utility";
import { AddUpdate } from "../../../ApiMethods/ApiMethods";
import { Modal } from "react-bootstrap";
import swal from "sweetalert";

const InterpretationRule = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [postModal, setPostModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addFormData, setAddFormData] = useState({
    Category: "",
    RuleCode: "",
    ConditionText: "",
    Observation: "",
    Priority: null,
  });
  const [categoryList, setCategoryList] = useState([]);

  const fetchData = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await AddUpdate(
        "AstrologyInterpretationRules/GetData_AstrologyCategoryRules",
        {},
      );

      if (signal?.aborted) return;
      const parsed = JSON.parse(response?.data?.data || "{}");
      const rows = parsed?.Table || [];
      setData(rows);
    } catch (err) {
      if (signal?.aborted) return;
      console.error("Error fetching interpretation categories:", err);
      setError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async (signal) => {
    try {
      const response = await AddUpdate(
        "AstrologyInterpretationRules/GetData_AstrologyCategory",
        {},
      );

      if (signal?.aborted) return;
      const parsed = JSON.parse(response?.data?.data || "{}");
      const rows = parsed?.Table || [];

      setCategoryList(rows);
    } catch (err) {
      if (signal?.aborted) return;
      console.error("Error fetching categories:", err);
      setError("Failed to load categories");
    }
  }, []);

  useEffect(() => {
    const controller = { aborted: false };
    fetchData(controller);
    fetchCategories(controller);
    return () => {
      controller.aborted = true;
    };
  }, [fetchData, fetchCategories]);

  const columns = useMemo(
    () => [
      { name: "RuleId", selector: (row) => row.RuleId, sortable: true },
      { name: "Category", selector: (row) => row.Category, sortable: true },
      { name: "RuleCode", selector: (row) => row.RuleCode, sortable: true },
      {
        name: "ConditionText",
        selector: (row) => row.ConditionText,
        sortable: true,
      },
      {
        name: "Observation",
        selector: (row) => row.Observation,
        sortable: true,
      },
    ],
    [],
  );

  const resetForm = useCallback(() => {
    setAddFormData({
      Category: "",
      RuleCode: "",
      ConditionText: "",
      Observation: "",
      Priority: null,
    });
  }, []);

  const handleAddFormSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!addFormData.Category?.toString().trim()) {
        swal("Validation", "Category is required", "warning");
        return;
      }
      setSaving(true);
      try {
        const payload = {
          Category: addFormData.Category,
          RuleCode: addFormData.RuleCode,
          ConditionText: addFormData.ConditionText,
          Observation: addFormData.Observation,
          Priority: null,
          CreatedByUser: "1",
        };
        const response = await AddUpdate(
          "AstrologyInterpretationRules/Insert_AstrologyCategoryRules",
          payload,
        );
        if (response) {
          swal("Success", "Category added", "success");
          setPostModal(false);
          resetForm();
          fetchData();
        }
      } catch (err) {
        console.error("Error adding category:", err);
        swal("Error", "Could not add category", "error");
      } finally {
        setSaving(false);
      }
    },
    [addFormData, fetchData, resetForm],
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="text-warning fw-bold">Interpretation Rules</h2>
        <button className="btn btn-primary" onClick={() => setPostModal(true)}>
          Add Interpretation Rule
        </button>
      </div>

      <div className="col-12 mt-2">
        <DataTable
          dense
          data={data}
          columns={columns}
          selectableRowsHighlight
          highlightOnHover
          pagination
          fixedHeader
          customStyles={customStyles}
          persistTableHead
          noDataComponent={
            loading ? "Loading..." : "There are no data to display"
          }
        />
        {error && <div className="text-danger mt-2">{error}</div>}
      </div>

      <Modal
        show={postModal}
        onHide={() => setPostModal(false)}
        backdrop="static"
      >
        <form onSubmit={handleAddFormSubmit}>
          <div className="modal-header">
            <h4 className="modal-title fs-20">Add Interpretation Rule</h4>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => {
                setPostModal(false);
                resetForm();
              }}
            />
          </div>
          <div className="modal-body p-3">
            <div className="form-group">
              <label htmlFor="category-select" className="form-label">
                Category <span className="text-danger">*</span>
              </label>
              <select
                id="category-select"
                className="form-control"
                value={addFormData.Category}
                onChange={(e) =>
                  setAddFormData({ ...addFormData, Category: e.target.value })
                }
                disabled={saving}
                required
              >
                <option value="">Select category</option>
                {categoryList.map((row, idx) => {
                  const val =
                    row.Category ??
                    row.Name ??
                    row.CategoryId ??
                    row.id ??
                    row.Text ??
                    "";
                  const label = row.Category ?? row.Name ?? row.Text ?? val;
                  return (
                    <option key={idx} value={val}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="rulecode-input" className="form-label">
                Rule Code
              </label>
              <input
                id="rulecode-input"
                type="text"
                className="form-control"
                autoComplete="off"
                value={addFormData.RuleCode}
                onChange={(e) =>
                  setAddFormData({ ...addFormData, RuleCode: e.target.value })
                }
                placeholder="Enter RuleCode"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="condition-input" className="form-label">
                Condition Text
              </label>
              <input
                id="condition-input"
                type="text"
                className="form-control"
                autoComplete="off"
                value={addFormData.ConditionText}
                onChange={(e) =>
                  setAddFormData({
                    ...addFormData,
                    ConditionText: e.target.value,
                  })
                }
                placeholder="Enter ConditionText"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="observation-input" className="form-label">
                Observation
              </label>
              <input
                id="observation-input"
                type="text"
                className="form-control"
                autoComplete="off"
                value={addFormData.Observation}
                onChange={(e) =>
                  setAddFormData({
                    ...addFormData,
                    Observation: e.target.value,
                  })
                }
                placeholder="Enter Observation"
                disabled={saving}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setPostModal(false);
                resetForm();
              }}
              className="btn btn-danger"
            >
              Close
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default InterpretationRule;
